import {apiClient} from 'common/util/api-client';
import {
  ProjectShareGrant,
  SharableEntityGrant,
  SharableEntityType,
  TeamShareGrant,
  UserShareGrant,
} from 'common/report/util/saved-entities/types';
import {isTestEnv} from 'common/report/util/env';

import {deleteFromFetchCache} from '../utils/fetch-cache';

interface SharableEntityRequest {
  projectId: number;
  entityId: number;
  entityType: SharableEntityType;
}

interface SharableEntityGrantUpdate {
  id: number;
  projectShares?: ProjectShareGrant[];
  userShares?: UserShareGrant[];
  teamShares?: TeamShareGrant[];
}

interface SharableEntityGrantDelete {
  id: number;
  /** Array of project IDs to remove (should only ever be at most 1) */
  projectShares?: number[];
  /** Array of user emails to remove */
  userShares?: string[];
  /** Array of team IDs to remove */
  teamShares?: number[];
}

interface RecentGrants {
  shared: SharableEntityGrant[];
  updated: SharableEntityGrant[];
}

class SharedEntitiesClient {
  async getGrants({projectId, entityId, entityType}: SharableEntityRequest): Promise<SharableEntityGrant> {
    const {cache, network} = apiClient.hydratedGet<SharableEntityGrant>(
      `/api/app/projects/${projectId}/shared-entities/${entityType}/${entityId}`,
    );
    const cachedResult = await cache;
    if (cachedResult && !isTestEnv()) {
      return cachedResult;
    }
    return network;
  }

  updateGrants(
    {projectId, entityId, entityType}: SharableEntityRequest,
    {
      projectShares,
      userShares,
      teamShares,
    }: {projectShares?: ProjectShareGrant[]; userShares?: UserShareGrant[]; teamShares?: TeamShareGrant[]},
  ): Promise<SharableEntityGrant> {
    const payload: SharableEntityGrantUpdate = {id: entityId};
    if (projectShares) {
      payload.projectShares = projectShares;
    }
    if (userShares) {
      payload.userShares = userShares;
    }
    if (teamShares) {
      payload.teamShares = teamShares;
    }

    // Delete existing value from fetchCache because we are about to modify the object
    deleteFromFetchCache(`/api/app/projects/${projectId}/shared-entities/${entityType}/${entityId}`, {
      method: `GET`,
    });

    return apiClient.post(`/api/app/projects/${projectId}/shared-entities/${entityType}/${entityId}/upsert`, payload);
  }

  removeGrants(
    {projectId, entityId, entityType}: SharableEntityRequest,
    {projectShares, userShares, teamShares}: {projectShares?: number[]; userShares?: string[]; teamShares?: number[]},
  ): Promise<SharableEntityGrant> {
    const payload: SharableEntityGrantDelete = {id: entityId};
    if (projectShares) {
      payload.projectShares = projectShares;
    }
    if (userShares) {
      payload.userShares = userShares;
    }
    if (teamShares) {
      payload.teamShares = teamShares;
    }

    // Delete existing value from fetchCache because we are about to modify the object
    deleteFromFetchCache(`/api/app/projects/${projectId}/shared-entities/${entityType}/${entityId}`, {
      method: `GET`,
    });

    return apiClient.post(`/api/app/projects/${projectId}/shared-entities/${entityType}/${entityId}/delete`, payload);
  }

  async recent({projectId, entityType}: {projectId: number; entityType: SharableEntityType}): Promise<RecentGrants> {
    const {cache, network} = apiClient.hydratedGet<RecentGrants>(
      `/api/app/projects/${projectId}/shared-entities/${entityType}/recent`,
    );
    const cachedResult = await cache;
    if (cachedResult && !isTestEnv()) {
      return cachedResult;
    }
    return network;
  }
}

export const sharedEntitiesClient = new SharedEntitiesClient();
